/**
 * AccessorySuggestionPanel — K3/2 + Csomag-Bérgép Link (2026-04-10)
 * Auto-suggests accessory packages based on the selected rental equipment.
 * Uses the unified `GET /packages/for-equipment/:equipmentId` endpoint which
 * returns both EQUIPMENT-mode (explicit product link) AND CATEGORY-mode
 * (category-based) packages in a single query. Integrated into EquipmentStep.
 */

import type { Package } from '@/api/packages';
import { Button, Card, CardContent } from '@/components/ui';
import { usePackagesForEquipment } from '@/hooks/use-packages';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import * as React from 'react';
import { useWizardStore } from '../wizard-store';

interface AccessorySuggestionPanelProps {
  selectedEquipmentIds: string[]; // RentalEquipment UUID-k a cart-ból
}

export function AccessorySuggestionPanel({ selectedEquipmentIds }: AccessorySuggestionPanelProps) {
  const { t } = useTranslation();
  const {
    accessoryPackage,
    accessoryQuantities,
    setAccessoryPackage,
    clearAccessoryPackage,
    updateAccessoryQuantity,
  } = useWizardStore();

  // Deduplicate equipment IDs, use the first for the API call.
  // TODO: multi-equipment cart → concat packages from all, dedupe by id
  const uniqueEquipmentIds = [...new Set(selectedEquipmentIds)];
  const firstEquipmentId = uniqueEquipmentIds[0];

  // Clear accessory package when the selected equipment changes
  const prevEquipmentRef = React.useRef(firstEquipmentId);
  React.useEffect(() => {
    if (firstEquipmentId !== prevEquipmentRef.current) {
      clearAccessoryPackage();
      prevEquipmentRef.current = firstEquipmentId;
    }
  }, [firstEquipmentId, clearAccessoryPackage]);

  const { packages, isLoading } = usePackagesForEquipment(firstEquipmentId);

  if (isLoading || packages.length === 0) return null;

  return (
    <div className="mt-6 space-y-4">
      <h3 className="text-lg font-medium text-foreground">
        {t('rental.wizard.suggestedAccessories')}
      </h3>

      {packages.map(pkg => {
        const isSelected = accessoryPackage?.id === pkg.id;
        return (
          <PackageCard
            key={pkg.id}
            pkg={pkg}
            isSelected={isSelected}
            accessoryQuantities={accessoryQuantities}
            onSelect={() => setAccessoryPackage(pkg)}
            onRemove={() => clearAccessoryPackage()}
            onUpdateQty={(itemId: string, qty: number) => updateAccessoryQuantity(itemId, qty)}
            t={t}
          />
        );
      })}
    </div>
  );
}

interface PackageCardProps {
  pkg: Package;
  isSelected: boolean;
  accessoryQuantities: Record<string, number>;
  onSelect: () => void;
  onRemove: () => void;
  onUpdateQty: (itemId: string, qty: number) => void;
  t: (key: string) => string;
}

function PackageCard({
  pkg,
  isSelected,
  accessoryQuantities,
  onSelect,
  onRemove,
  onUpdateQty,
  t,
}: PackageCardProps) {
  return (
    <Card className={cn(isSelected && 'ring-2 ring-primary')}>
      <CardContent className="p-4">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h4 className="font-medium text-foreground">{pkg.name}</h4>
            {pkg.description && <p className="text-sm text-muted-foreground">{pkg.description}</p>}
          </div>
          <Button
            variant={isSelected ? 'destructive' : 'default'}
            size="sm"
            onClick={isSelected ? onRemove : onSelect}
            className="shrink-0"
          >
            {isSelected
              ? t('rental.wizard.removeAccessoryPackage')
              : t('rental.wizard.selectAccessoryPackage')}
          </Button>
        </div>

        {/* Items list — shown only when selected */}
        {isSelected && pkg.items.length > 0 && (
          <div className="mt-4 space-y-2">
            {pkg.items.map(item => {
              const qty = accessoryQuantities[item.id] ?? item.quantity;
              return (
                <div
                  key={item.id}
                  className={cn(
                    'flex items-center gap-3 rounded-md px-2 py-1',
                    qty === 0 && 'opacity-50'
                  )}
                >
                  {/* Item name */}
                  <span className="flex-1 text-sm text-foreground">
                    {item.notes ?? item.productId ?? item.id}
                  </span>

                  {/* Settlement badge */}
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
                      item.settlement === 'INCLUDED'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'border border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400'
                    )}
                  >
                    {item.settlement === 'INCLUDED'
                      ? t('rental.wizard.included')
                      : t('rental.wizard.consignment')}
                  </span>

                  {/* Quantity stepper */}
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 dark:border-[#2d7d8a] text-muted-foreground hover:bg-muted"
                      onClick={() => onUpdateQty(item.id, Math.max(0, qty - 1))}
                      aria-label="-"
                    >
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 12H4"
                        />
                      </svg>
                    </button>
                    <span className="w-8 text-center text-sm font-medium text-foreground">
                      {qty}
                    </span>
                    <button
                      type="button"
                      className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 dark:border-[#2d7d8a] text-muted-foreground hover:bg-muted"
                      onClick={() => onUpdateQty(item.id, qty + 1)}
                      aria-label="+"
                    >
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
